import React, { useState, useEffect, useMemo, useCallback } from 'react';
import axios from 'axios';
import SchoolAvatar from '../SchoolAvatar';
import SchoolsMap from './SchoolsMap';
import PlacesMap from './PlacesMap';
import SportTypeSelector from '../SportTypeSelector';
import { fetchSportTypeCatalog, flattenSportCatalog } from '../../api/sportTypes';
import { getApiBaseUrl } from '../../apiBase';
import { hasSchoolCoordinates } from '../../utils/schoolCoordinates';
import { useTranslation } from 'react-i18next';

/**
 * Поиск школ: список + карта, фильтр по виду спорта (по умолчанию — вид пользователя).
 */
export default function SchoolDiscoveryPanel({
    api,
    onApply,
    applyLabel: applyLabelProp,
    showMyApps = null,
    renderActionButton = null
}) {
    const { t } = useTranslation(['schoolDiscovery', 'common']);
    const applyLabel = applyLabelProp ?? t('schoolDiscovery:apply');
    const [schools, setSchools] = useState([]);
    const [search, setSearch] = useState('');
    const [city, setCity] = useState('');
    const [loading, setLoading] = useState(false);
    const [selectedSchool, setSelectedSchool] = useState(null);
    const [places, setPlaces] = useState([]);
    const [view, setView] = useState('both'); // list | map | both

    const userSportTypeId = useMemo(() => {
        const id = parseInt(localStorage.getItem('sportTypeId') || '', 10);
        return Number.isFinite(id) && id > 0 ? id : null;
    }, []);

    const userSportTypeName = localStorage.getItem('sportType') || '';

    /** mine | all | custom */
    const [sportMode, setSportMode] = useState(userSportTypeId ? 'mine' : 'all');
    const [customSportTypeId, setCustomSportTypeId] = useState(userSportTypeId ? String(userSportTypeId) : '');
    const [sportNames, setSportNames] = useState({});

    useEffect(() => {
        let cancelled = false;
        fetchSportTypeCatalog()
            .then((groups) => {
                if (cancelled) return;
                const map = {};
                flattenSportCatalog(groups).forEach((s) => { map[s.id] = s.name; });
                setSportNames(map);
            })
            .catch(() => {});
        return () => { cancelled = true; };
    }, []);

    const activeSportTypeId = useMemo(() => {
        if (sportMode === 'all') return null;
        if (sportMode === 'mine') return userSportTypeId;
        const id = parseInt(customSportTypeId, 10);
        return Number.isFinite(id) && id > 0 ? id : null;
    }, [sportMode, userSportTypeId, customSportTypeId]);

    const loadSchools = useCallback(async () => {
        setLoading(true);
        try {
            const params = {};
            if (search.trim()) params.search = search.trim();
            if (city.trim()) params.city = city.trim();
            if (activeSportTypeId) params.sportTypeId = activeSportTypeId;
            const res = await api.get('/application/schools', { params });
            setSchools(res.data || []);
            // Места на карте: одна метка на здание. Если мест ещё нет (или сервис недоступен) — показываем старую карту по школам.
            try {
                const placesRes = await api.get('/places/map', { params });
                setPlaces(placesRes.data || []);
            } catch {
                setPlaces([]);
            }
        } catch {
            setSchools([]);
        }
        setLoading(false);
    }, [api, search, city, activeSportTypeId]);

    useEffect(() => {
        loadSchools();
    }, [loadSchools]);

    const mapSchools = useMemo(
        () => schools.filter((s) => hasSchoolCoordinates(s) || s.hasCoordinates === true),
        [schools]
    );

    const sportFilterLabel = useMemo(() => {
        if (sportMode === 'all') return t('schoolDiscovery:allSports');
        if (sportMode === 'mine') {
            const name = userSportTypeName || sportNames[userSportTypeId] || t('schoolDiscovery:mySportFallback');
            return name;
        }
        return sportNames[parseInt(customSportTypeId, 10)] || t('schoolDiscovery:customSportFallback');
    }, [sportMode, userSportTypeName, sportNames, userSportTypeId, customSportTypeId, t]);

    function handleSelectFromMap(school) {
        setSelectedSchool(school);
        const el = document.getElementById(`school-card-${school.id}`);
        el?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    }

    const chip = (active) => ({
        padding: '8px 14px',
        borderRadius: 999,
        border: active ? '2px solid #EB3333' : '1.5px solid var(--sp-gray-200)',
        background: active ? '#f5f3ff' : '#fff',
        color: active ? '#5b21b6' : '#475569',
        fontWeight: active ? 700 : 500,
        fontSize: 13,
        cursor: 'pointer'
    });

    return (
        <div>
            <div style={{ display: 'flex', gap: 10, marginBottom: 16, flexWrap: 'wrap' }}>
                <input
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    onKeyDown={(e) => e.key === 'Enter' && loadSchools()}
                    placeholder={t('schoolDiscovery:searchPlaceholder')}
                    style={{ flex: 1, minWidth: 180, padding: '12px 14px', borderRadius: 10, border: '1.5px solid var(--sp-gray-200)' }}
                />
                <input
                    value={city}
                    onChange={(e) => setCity(e.target.value)}
                    onKeyDown={(e) => e.key === 'Enter' && loadSchools()}
                    placeholder={t('schoolDiscovery:cityPlaceholder')}
                    style={{ width: 160, padding: '12px 14px', borderRadius: 10, border: '1.5px solid var(--sp-gray-200)' }}
                />
                <button
                    type="button"
                    onClick={loadSchools}
                    disabled={loading}
                    style={{
                        padding: '12px 20px', borderRadius: 10, border: 'none',
                        background: loading ? '#94a3b8' : '#EB3333', color: '#fff',
                        fontWeight: 700, cursor: loading ? 'wait' : 'pointer'
                    }}
                >
                    {loading ? '…' : t('schoolDiscovery:search')}
                </button>
            </div>

            <div style={{
                background: '#fff', borderRadius: 12, padding: 14, marginBottom: 16,
                border: '1px solid #e8ecf4'
            }}>
                <div style={{ fontSize: 13, fontWeight: 700, color: '#334155', marginBottom: 10 }}>
                    {t('schoolDiscovery:sportFilterTitle')}
                </div>
                <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: sportMode === 'custom' ? 12 : 0 }}>
                    {userSportTypeId ? (
                        <button type="button" style={chip(sportMode === 'mine')} onClick={() => setSportMode('mine')}>
                            {userSportTypeName ? t('schoolDiscovery:mySportPrefix', { name: userSportTypeName }) : t('schoolDiscovery:mySport')}
                        </button>
                    ) : null}
                    <button type="button" style={chip(sportMode === 'custom')} onClick={() => setSportMode('custom')}>
                        {t('schoolDiscovery:otherSport')}
                    </button>
                    <button type="button" style={chip(sportMode === 'all')} onClick={() => setSportMode('all')}>
                        {t('schoolDiscovery:allSportsShort')}
                    </button>
                </div>
                {sportMode === 'custom' && (
                    <div style={{ marginTop: 4 }}>
                        <SportTypeSelector
                            value={customSportTypeId}
                            onChange={(id) => setCustomSportTypeId(String(id))}
                            compact
                            showSoon={false}
                        />
                    </div>
                )}
                <div style={{ fontSize: 12, color: '#64748b', marginTop: 10 }}>
                    {t('schoolDiscovery:filterCurrent', { label: sportFilterLabel })}
                    {' · '}
                    {t('schoolDiscovery:schoolsCount', { count: schools.length })}
                    {mapSchools.length > 0 && mapSchools.length !== schools.length
                        ? t('schoolDiscovery:onMapCount', { count: mapSchools.length })
                        : mapSchools.length > 0 && mapSchools.length === schools.length
                            ? t('schoolDiscovery:onMapCount', { count: mapSchools.length })
                            : ''}
                </div>
            </div>

            <div style={{ display: 'flex', gap: 8, marginBottom: 12 }}>
                {[
                    { id: 'both', label: t('schoolDiscovery:viewBoth') },
                    { id: 'map', label: t('schoolDiscovery:viewMap') },
                    { id: 'list', label: t('schoolDiscovery:viewList') }
                ].map((v) => (
                    <button
                        key={v.id}
                        type="button"
                        onClick={() => setView(v.id)}
                        style={{
                            padding: '6px 12px', borderRadius: 8, fontSize: 12, fontWeight: 600,
                            border: view === v.id ? 'none' : '1px solid var(--sp-gray-200)',
                            background: view === v.id ? '#1A1A1A' : '#fff',
                            color: view === v.id ? '#fff' : '#64748b',
                            cursor: 'pointer'
                        }}
                    >
                        {v.label}
                    </button>
                ))}
            </div>

            {showMyApps}

            <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
                {(view === 'both' || view === 'map') && (
                    places.length > 0 ? (
                        <PlacesMap
                            places={places}
                            selectedSchool={selectedSchool}
                            onSelectSchool={handleSelectFromMap}
                            height={view === 'map' ? '520px' : '420px'}
                        />
                    ) : (
                        <SchoolsMap
                            schools={schools}
                            selectedSchool={selectedSchool}
                            onSelectSchool={handleSelectFromMap}
                            height={view === 'map' ? '520px' : '420px'}
                        />
                    )
                )}
                {(view === 'both' || view === 'list') && (
                    <div style={{
                        display: 'grid',
                        gap: 12,
                        maxHeight: view === 'both' ? 480 : 'none',
                        overflowY: view === 'both' ? 'auto' : 'visible'
                    }}>
                        {schools.map((s) => {
                            const onMap = hasSchoolCoordinates(s) || s.hasCoordinates === true;
                            const isSel = selectedSchool?.id === s.id;
                            return (
                                <div
                                    id={`school-card-${s.id}`}
                                    key={s.id}
                                    style={{
                                        background: '#fff', borderRadius: 14, padding: 18,
                                        border: isSel ? '2px solid #EB3333' : '1px solid #e8ecf4',
                                        display: 'flex', gap: 14, alignItems: 'center',
                                        boxShadow: isSel ? '0 4px 20px rgba(108,63,232,0.15)' : 'none'
                                    }}
                                >
                                    <SchoolAvatar logoPath={s.logo} size={48} alt={s.name} />
                                    <div style={{ flex: 1, minWidth: 0 }}>
                                        <div style={{ fontWeight: 700, fontSize: 16 }}>{s.name}</div>
                                        <div style={{ color: '#64748b', fontSize: 13, marginTop: 4 }}>
                                            📍 {s.city || '—'} · {s.sportIcon} {s.sportType}
                                            {!onMap && (
                                        <span style={{ color: '#f59e0b' }}>
                                            {' '}{t('schoolDiscovery:noCoordsHint')}
                                        </span>
                                    )}
                                        </div>
                                        {(s.addressFull || s.address) && (
                                            <div style={{ color: '#94a3b8', fontSize: 12, marginTop: 2 }}>
                                                {s.addressFull || s.address}
                                            </div>
                                        )}
                                    </div>
                                    {renderActionButton ? renderActionButton(s) : (
                                        <button
                                            type="button"
                                            onClick={() => onApply(s)}
                                            style={{
                                                padding: '10px 16px', borderRadius: 10, border: 'none',
                                                background: '#1A1A1A', color: '#fff', fontWeight: 600,
                                                cursor: 'pointer', whiteSpace: 'nowrap'
                                            }}
                                        >
                                            {applyLabel}
                                        </button>
                                    )}
                                </div>
                            );
                        })}
                        {schools.length === 0 && !loading && (
                            <p style={{ color: '#888', textAlign: 'center' }}>{t('schoolDiscovery:empty')}</p>
                        )}
                    </div>
                )}
            </div>
        </div>
    );
}


/** Хелпер для страниц с axios */
export function useDiscoveryApi() {
    const token = localStorage.getItem('token');
    return useMemo(() => axios.create({
        baseURL: getApiBaseUrl(),
        headers: token ? { Authorization: `Bearer ${token}` } : {}
    }), [token]);
}
